import { ListenableSubscribable, Subscription } from '@benzinga/subscribable';
import { UserSettingsManager } from '@benzinga/user-settings';
import { Session } from '@benzinga/session';
import { ProUserSettingsFeed, ProUserSetting } from './entities';
import { ProUserSettingConfig } from './ingress';

interface ProUserSettingsUpdateEvent {
  type: 'pro_user_settings:update';
  setting: ProUserSetting;
}

interface ProUserSettingUpdateEvent {
  type: 'pro_user_setting:update';
  key: keyof ProUserSetting;
  value: ProUserSetting[keyof ProUserSetting];
}

export type ProUserSettingsManagerEvent = ProUserSettingsUpdateEvent | ProUserSettingUpdateEvent;

export class ProUserSettingsManager extends ListenableSubscribable<ProUserSettingsManagerEvent> {
  private settingsFeed: ProUserSettingsFeed;
  private feedSubscription?: Subscription<ProUserSettingsFeed>;

  constructor(session: Session) {
    super();
    this.settingsFeed = session.getManager(UserSettingsManager).getFeed(ProUserSettingConfig);
  }

  public static getName = () => 'benzinga-pro-user-settings';

  public resetSettings = () => {
    const newSettings = ProUserSettingConfig.loader(undefined, undefined);
    Object.keys(newSettings).forEach(key => {
      this.setSetting(key as keyof ProUserSetting, newSettings[key as keyof ProUserSetting]);
    });
  };

  public setSetting: <K extends keyof ProUserSetting>(
    key: K,
    value: ProUserSetting[K] | ((val: ProUserSetting[K]) => ProUserSetting[K]),
  ) => void = (key, value) => {
    this.settingsFeed.setValue(settings => {
      const v = (() => {
        if (typeof value === 'function') {
          return value(settings[key]);
        } else {
          return value;
        }
      })();
      const newSettings = { ...settings, [key]: v };
      this.dispatch({
        setting: newSettings,
        type: 'pro_user_settings:update',
      });
      this.dispatch({
        key,
        type: 'pro_user_setting:update',
        value: v,
      });
      return { ...settings, [key]: v };
    });
  };
  public getSettings = () => {
    return this.settingsFeed.getValue();
  };
  public getOldSettings = () => {
    return this.settingsFeed.getRawValue();
  };

  override onFirstSubscription = (): void => {
    this.feedSubscription = this.settingsFeed.subscribe(event => {
      if (event.type === 'settings_feed:update') {
        this.dispatch({
          setting: event.value,
          type: 'pro_user_settings:update',
        });
      }
    });
  };

  protected override onZeroSubscriptions(): void {
    this.feedSubscription?.unsubscribe();
    this.feedSubscription = undefined;
  }
}
